/**
 * Mix one or more MediaStreams that carry audio into a single recordable stream.
 * Used to combine microphone + getDisplayMedia tab/system audio for pitch takes.
 */

export function liveAudioTracks(stream) {
  return (stream?.getAudioTracks?.() || []).filter((t) => t && t.readyState === 'live');
}

export function streamHasLiveAudio(stream) {
  return liveAudioTracks(stream).length > 0;
}

/**
 * @param {Array<MediaStream|null|undefined>} streams
 * @returns {{ stream: MediaStream|null, close: () => void, mixed: boolean }}
 */
export function mixPitchAudioStreams(streams) {
  const usable = (streams || []).filter((s) => streamHasLiveAudio(s));
  if (!usable.length) {
    return { stream: null, close: () => {}, mixed: false };
  }
  if (usable.length === 1) {
    return { stream: usable[0], close: () => {}, mixed: false };
  }

  const AC = typeof window !== 'undefined'
    ? (window.AudioContext || window.webkitAudioContext)
    : null;
  if (!AC) {
    return { stream: usable[0], close: () => {}, mixed: false };
  }

  const ctx = new AC();
  const dest = ctx.createMediaStreamDestination();
  const nodes = [];
  for (const stream of usable) {
    try {
      const src = ctx.createMediaStreamSource(stream);
      src.connect(dest);
      nodes.push(src);
    } catch (err) {
      console.warn('Pitch audio mix: skip stream', err);
    }
  }

  if (!nodes.length) {
    try {
      ctx.close();
    } catch {
      /* ignore */
    }
    return { stream: usable[0], close: () => {}, mixed: false };
  }

  return {
    stream: dest.stream,
    mixed: true,
    close: () => {
      nodes.forEach((n) => {
        try {
          n.disconnect();
        } catch {
          /* ignore */
        }
      });
      try {
        ctx.close();
      } catch {
        /* ignore */
      }
    },
  };
}
